import React, { useState } from 'react';
import {
  Zap,
  ShieldCheck,
  ArrowUpRight,
  Database,
  Crosshair,
  TrendingUp,
  LineChart,
  Activity
} from 'lucide-react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { useConvergenceRadar, ConvergenceStock } from '../../hooks/useConvergenceRadar';

/** Normalise une valeur dans [minVal, maxVal] vers [pctMin, pctMax] en % */
function normalize(value: number, minVal: number, maxVal: number, pctMin: number, pctMax: number): number {
  if (maxVal === minVal) return (pctMin + pctMax) / 2;
  const ratio = (value - minVal) / (maxVal - minVal);
  return pctMin + ratio * (pctMax - pctMin);
}

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function formatPotentiel(pct: number): string {
  return `${pct >= 0 ? '+' : ''}${pct}%`;
}

function formatScore(score: number): string {
  return `${score > 0 ? '+' : ''}${score}`;
}

const ConvergenceRadarScreen: React.FC = () => {
  const { stocks, meta, loading, error } = useConvergenceRadar();
  const [hovered, setHovered] = useState<string | null>(null);

  const potentielThreshold = meta.sweet_spot_thresholds.potentiel_pct;
  const scoreThreshold = meta.sweet_spot_thresholds.tech_score;

  const renderMatrix = () => {
    const containerStyle: React.CSSProperties = {
      position: 'relative',
      width: '100%',
      height: '520px',
      backgroundColor: '#111114',
      backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)',
      backgroundSize: '40px 40px',
      borderRadius: '20px',
      border: '1px solid rgba(255,255,255,0.1)',
      overflow: 'hidden',
      display: 'block'
    };

    // Cas vide : ne pas calculer min/max sur un tableau vide (Infinity dans les graduations)
    if (stocks.length === 0) {
      return (
        <div style={containerStyle}>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.2 }}>
            <p style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '4px' }}>Aucun signal actif</p>
          </div>
        </div>
      );
    }

    // Normalisation dynamique sur la plage réelle des données
    const potentials = stocks.map(s => s.potentiel_pct);
    const scores     = stocks.map(s => s.tech_score);

    const minPotentiel = Math.min(...potentials);
    const maxPotentiel = Math.max(...potentials);
    const minScore     = Math.min(...scores);
    const maxScore     = Math.max(...scores);

    // Graduation labels pour les axes (le milieu = vrai milieu de la plage, pas 0)
    const xLabels = [
      { label: `${maxPotentiel.toFixed(0)}%`, pct: 10 },
      { label: `${((minPotentiel + maxPotentiel) / 2).toFixed(0)}%`, pct: 50 },
      { label: `${minPotentiel.toFixed(0)}%`, pct: 90 },
    ];
    const yLabels = [
      { label: formatScore(Number(maxScore.toFixed(1))), pct: 10 },
      { label: formatScore(Number(((minScore + maxScore) / 2).toFixed(1))), pct: 50 },
      { label: formatScore(Number(minScore.toFixed(1))), pct: 90 },
    ];

    // Zone Sweet Spot positionnée sur les SEUILS RÉELS (et non un quadrant 50/50 arbitraire).
    // X inversé : fort potentiel = gauche. La zone couvre potentiel >= seuil ET score >= seuil.
    const sweetSpotReachable = maxPotentiel >= potentielThreshold && maxScore >= scoreThreshold;
    const zoneRight  = clampPct(normalize(potentielThreshold, minPotentiel, maxPotentiel, 90, 10));
    const zoneBottom = clampPct(normalize(scoreThreshold, minScore, maxScore, 90, 10));

    return (
      <div style={containerStyle}>
        {/* Axis Labels */}
        <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%) rotate(-90deg)', fontSize: '9px', fontWeight: 'bold', color: '#6366f1', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          Momentum Technique ↑
        </div>
        <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', fontSize: '9px', fontWeight: 'bold', color: '#22c55e', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          ← Sous-évalué (Potentiel %) · Surévalué →
        </div>

        {/* Y-axis graduations */}
        {yLabels.map((g, i) => (
          <div key={i} style={{
            position: 'absolute', left: '30px', top: `${g.pct}%`,
            fontSize: '8px', color: 'rgba(255,255,255,0.25)',
            transform: 'translateY(-50%)',
          }}>{g.label}</div>
        ))}

        {/* X-axis graduations */}
        {xLabels.map((g, i) => (
          <div key={i} style={{
            position: 'absolute', bottom: '22px', left: `${g.pct}%`,
            fontSize: '8px', color: 'rgba(255,255,255,0.25)',
            transform: 'translateX(-50%)',
          }}>{g.label}</div>
        ))}

        {/* Sweet Spot Highlight — bordé par les seuils réels (potentiel & score) */}
        {sweetSpotReachable && (
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: `${zoneRight}%`, height: `${zoneBottom}%`,
            backgroundColor: 'rgba(99,102,241,0.04)',
            borderBottom: '1px dashed rgba(99,102,241,0.25)',
            borderRight: '1px dashed rgba(99,102,241,0.25)'
          }}>
            <span style={{ position: 'absolute', top: '12px', left: '12px', color: '#818cf8', fontSize: '9px', fontWeight: 'bold', textTransform: 'uppercase' }}>
              Sweet Spot 💎 (≥ {potentielThreshold}% · score ≥ {formatScore(scoreThreshold)})
            </span>
          </div>
        )}

        {/* Plotting stocks — position calculée dynamiquement */}
        {stocks.map((s, idx) => {
          // X : fort potentiel = gauche. Inverser l'axe potentiel (max → gauche = 10%, min → droite = 90%)
          const x = normalize(s.potentiel_pct, minPotentiel, maxPotentiel, 90, 10);
          // Y : fort score = haut. Inverser l'axe score (max → haut = 10%, min → bas = 90%)
          const y = normalize(s.tech_score, minScore, maxScore, 90, 10);

          const isHovered = hovered === s.symbole;

          return (
            <div
              key={idx}
              onMouseEnter={() => setHovered(s.symbole)}
              onMouseLeave={() => setHovered(null)}
              title={`${s.symbole}: ${formatPotentiel(s.potentiel_pct)} Potentiel, Score Tech: ${formatScore(s.tech_score)}${s.is_speculative ? ' · ⚠ Fiabilité spéculative' : ''}${s.date_calcul ? ` · Valo du ${s.date_calcul}` : ''}`}
              style={{
                position: 'absolute',
                left: `${x}%`,
                top: `${y}%`,
                width: s.is_sweet_spot ? '16px' : '12px',
                height: s.is_sweet_spot ? '16px' : '12px',
                backgroundColor: s.is_speculative ? '#78350f' : (s.is_sweet_spot ? '#818cf8' : (s.tech_score > 0 ? '#6366f1' : '#4b5563')),
                borderRadius: '50%',
                border: s.is_speculative ? '2px solid #f59e0b' : '2px solid white',
                boxShadow: s.is_sweet_spot ? '0 0 15px #818cf8' : 'none',
                transform: 'translate(-50%, -50%)',
                cursor: 'pointer',
                zIndex: isHovered ? 200 : (s.is_sweet_spot ? 100 : 10),
                transition: 'all 0.2s ease'
              }}
              onClick={() => window.open(`/data?symbol=${s.symbole}`, '_blank')}
            >
              {/* Label visible only on sweet spots or hover */}
              {(s.is_sweet_spot || isHovered) && (
                <span style={{
                  position: 'absolute',
                  top: s.is_sweet_spot ? '17px' : '-22px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: 'white',
                  whiteSpace: 'nowrap',
                  backgroundColor: isHovered ? 'rgba(20,20,30,0.95)' : 'rgba(0,0,0,0.65)',
                  padding: isHovered ? '3px 8px' : '1px 4px',
                  borderRadius: '5px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  pointerEvents: 'none',
                  zIndex: 300,
                }}>
                  {isHovered
                    ? `${s.symbole} · ${formatPotentiel(s.potentiel_pct)} · ${formatScore(s.tech_score)}`
                    : s.symbole}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Panel droit = seulement les "Diamants" (potentiel >= seuil sweet spot)
  const diamonds    = stocks.filter(s => s.potentiel_pct >= potentielThreshold);
  const otherStocks = stocks.filter(s => s.potentiel_pct < potentielThreshold);
  const totalSignals = stocks.reduce((sum, s) => sum + s.nb_signals, 0);

  const renderDiamondCard = (s: ConvergenceStock, idx: number) => (
    <div key={idx} style={{
      background: s.is_sweet_spot ? 'rgba(99,102,241,0.05)' : '#121217',
      padding: '16px',
      borderRadius: '12px',
      border: s.is_speculative ? '1px solid rgba(245,158,11,0.35)' : (s.is_sweet_spot ? '1px solid rgba(99,102,241,0.3)' : '1px solid rgba(255,255,255,0.05)'),
      position: 'relative',
      overflow: 'hidden'
    }}>
      {s.is_sweet_spot && (
        <div style={{ position: 'absolute', top: 0, right: 0, background: '#6366f1', color: 'white', fontSize: '8px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '0 0 0 8px' }}>SWEET SPOT</div>
      )}
      {s.is_speculative && (
        <div
          title="Valorisation peu fiable (< 4 modèles, faible poids capté ou forte divergence). Exclue du Sweet Spot — le backtest live montre un rendement réalisé négatif sur ce segment."
          style={{ position: 'absolute', top: 0, right: 0, background: '#78350f', color: '#fbbf24', fontSize: '8px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '0 0 0 8px', border: '1px solid rgba(245,158,11,0.4)' }}
        >⚠ SPÉCULATIF</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
        <div>
          <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{s.symbole}</span>
          <p style={{ margin: 0, fontSize: '10px', color: '#6b7280' }}>{s.nom_societe}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ color: s.potentiel_pct >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold', fontSize: '12px' }}>
            {formatPotentiel(s.potentiel_pct)}
          </span>
          <p style={{ margin: 0, fontSize: '9px', color: s.tech_score > 0 ? '#6366f1' : '#ef4444' }}>Score Tech: {formatScore(s.tech_score)}</p>
          {s.date_calcul && (
            <p style={{ margin: 0, fontSize: '8px', color: '#4b5563' }} title="Date du dernier calcul de valorisation">Valo du {s.date_calcul}</p>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {s.signals.slice(0, 3).map((sig, sIdx) => (
          <span key={sIdx} style={{ fontSize: '8px', background: 'rgba(255,255,255,0.05)', color: '#999', padding: '2px 6px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
            {sig.type}
          </span>
        ))}
      </div>

      <button
        onClick={() => window.open(`/data?symbol=${s.symbole}`, '_blank')}
        style={{
          marginTop: '12px',
          width: '100%',
          padding: '8px',
          borderRadius: '8px',
          background: 'rgba(255,255,255,0.05)',
          border: 'none',
          color: 'white',
          fontSize: '10px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        Analyse Pro <ArrowUpRight size={12} />
      </button>
    </div>
  );

  return (
    <SiteLayout>
      <Header icon="track_changes" title="Radar de Convergence" />

      <div style={{ padding: '24px', background: '#0a0a0c', minHeight: '100vh', color: 'white' }}>
        <Box>
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: '0 0 8px 0' }}>Matrice Opportunité/Risque</h2>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
                Intersection entre la <strong>Valorisation Fondamentale</strong> (Vrai Prix) et le <strong>Momentum Technique</strong>.
                Les axes sont normalisés sur la plage réelle des données.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ background: '#16161a', padding: '12px 20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Activity color="#818cf8" />
                <div>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 'bold', margin: 0, textTransform: 'uppercase' }}>Sweet Spots</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>{stocks.filter(s => s.is_sweet_spot).length}</p>
                </div>
              </div>
              <div style={{ background: '#16161a', padding: '12px 20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Activity color="#6366f1" />
                <div>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 'bold', margin: 0, textTransform: 'uppercase' }}>Signaux Détectés ({meta.window_days}j)</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>{totalSignals}</p>
                </div>
              </div>
            </div>
          </div>
        </Box>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '24px', marginTop: '24px' }}>
          <div style={{ background: '#16161a', padding: '24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)' }}>
             <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
               <Crosshair size={16} /> Vue Radar Alpha
               <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 'normal', color: '#374151' }}>Survol = détail · Clic = analyse</span>
             </h3>
             {loading ? (
               <div style={{ height: '520px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                 <p style={{ color: '#666' }}>Analyse des convergences en cours...</p>
               </div>
             ) : error ? (
               <div style={{ height: '520px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                 <p style={{ color: '#ef4444' }}>{error}</p>
               </div>
             ) : (
               renderMatrix()
             )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
             <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px', margin: 0 }}>
               <TrendingUp size={16} /> Opportunités Diamants <span style={{ fontSize: '10px', color: '#374151', fontWeight: 'normal' }}>(potentiel ≥ {potentielThreshold}%)</span>
             </h3>
             <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '600px', overflowY: 'auto', paddingRight: '8px' }}>
               {diamonds.map(renderDiamondCard)}
               {!loading && diamonds.length === 0 && (
                 <div style={{ padding: '40px 20px', textAlign: 'center', color: '#444' }}>
                    <Database size={32} style={{ marginBottom: '12px', opacity: 0.15 }} />
                    <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px', margin: 0 }}>Aucun diamant détecté</p>
                    <p style={{ fontSize: '10px', color: '#333', marginTop: '6px' }}>Seuil : potentiel ≥ {potentielThreshold}%</p>
                 </div>
               )}

               {/* Autres titres (potentiel < seuil) en section pliée */}
               {otherStocks.length > 0 && (
                 <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px' }}>
                   <p style={{ fontSize: '9px', color: '#374151', textTransform: 'uppercase', margin: '0 0 8px 0', letterSpacing: '1px' }}>
                     Autres signaux ({otherStocks.length})
                   </p>
                   {otherStocks.map((s, idx) => (
                     <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', borderRadius: '8px', background: '#0e0e12', marginBottom: '4px' }}>
                       <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{s.symbole}</span>
                       <div style={{ textAlign: 'right' }}>
                         <span style={{ fontSize: '11px', color: s.potentiel_pct >= 0 ? '#22c55e' : '#ef4444' }}>{formatPotentiel(s.potentiel_pct)}</span>
                         <span style={{ fontSize: '9px', color: '#374151', marginLeft: '6px' }}>T:{formatScore(s.tech_score)}</span>
                       </div>
                     </div>
                   ))}
                 </div>
               )}
             </div>
          </div>
        </div>

        <Box style={{ marginTop: '24px' }}>
           <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px' }}>
             <div>
               <h5 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                 <ShieldCheck size={14} color="#22c55e" /> Fondamental
               </h5>
               <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, lineHeight: '1.5' }}>
                 Axe Horizontal (X). Plus l'action est à <strong>gauche</strong>, plus elle est <strong>sous-évaluée</strong> selon nos 12 modèles de valorisation. L'axe est normalisé sur la plage réelle des données (positions relatives au marché du jour).
               </p>
             </div>
             <div>
               <h5 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                 <LineChart size={14} color="#6366f1" /> Momentum
               </h5>
               <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, lineHeight: '1.5' }}>
                 Axe Vertical (Y). Plus l'action est <strong>haute</strong>, plus elle accumule de signaux <strong>purement techniques</strong> récents (dédupliqués par type sur {meta.window_days} jours). Scoré de GOLDEN_CROSS (+3.5) à DEATH_CROSS (-3.5), pondéré par sévérité. Les signaux de valorisation sont exclus du score (déjà portés par l'axe X).
               </p>
             </div>
             <div>
               <h5 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', color: '#818cf8' }}>
                 <Zap size={14} /> Sweet Spot
               </h5>
               <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, lineHeight: '1.5' }}>
                 Zone délimitée par les seuils réels : <strong>potentiel ≥ {potentielThreshold}%</strong> ET <strong>score technique ≥ {formatScore(scoreThreshold)}</strong>. Les valorisations de fiabilité <strong style={{ color: '#f59e0b' }}>SPÉCULATIVE</strong> (points ambrés ⚠) sont exclues et rétrogradées : le backtest live montre un rendement réalisé négatif sur ce segment.
               </p>
             </div>
           </div>
        </Box>
      </div>
    </SiteLayout>
  );
};

export default ConvergenceRadarScreen;
